import axios from "axios";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [verify, setVerify] = useState({});
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const inputRefs = useRef([]);

  /* ================= EMAIL VALIDATION ================= */
  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  /* ================= OTP CHANGE ================= */
  const handleOtpChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    setVerify({ email, otp: newOtp.join("") });

    if (value && index < 3) {
      inputRefs.current[index + 1].focus();
    }
  };

  /* ================= OTP PASTE ================= */
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 4);

    if (!pastedData) return;

    const newOtp = pastedData.split("");
    const filledOtp = ["", "", "", ""];

    newOtp.forEach((num, i) => {
      filledOtp[i] = num;
    });

    setOtp(filledOtp);
    setVerify({ email, otp: filledOtp.join("") });

    inputRefs.current[Math.min(newOtp.length, 3)]?.focus();
  };

  /* ================= BACKSPACE ================= */
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  /* ================= SEND OTP ================= */
  const handleSendOtp = async (e) => {
    e.preventDefault();

    if (!email || !validateEmail(email)) {
      Swal.fire("Please enter valid email", "", "warning");
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/user/reqOTP`,
        { email }
      );

      if (res.status === 200) {
        setStep(2);
      } else {
        Swal.fire("Email not registered", "", "warning");
      }
    } catch {
      Swal.fire("Something went wrong", "", "error");
    }
  };

  /* ================= VERIFY OTP ================= */
  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (verify.otp?.length !== 4) {
      Swal.fire("Please enter valid OTP", "", "warning");
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/user/verifyOTP`,
        verify
      );

      if (res.status === 200) {
        setStep(3);
      } else {
        Swal.fire("Invalid OTP", "", "warning");
      }
    } catch {
      Swal.fire("OTP verification failed", "", "error");
    }
  };

  /* ================= CHANGE PASSWORD ================= */
  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!password) {
      Swal.fire("Please enter password", "", "warning");
      return;
    }

    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/user/forgetPassword`,
        { email, password }
      );

      if (res.status === 200) {
        Swal.fire("Password updated successfully", "", "success").then(() =>
          navigate("/login")
        );
      }
    } catch {
      Swal.fire("Password update failed", "", "error");
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        {/* HEADER */}
        <div className="login-header">
          <img src="/assets/img/ca-logo.jpg" className="logo" alt="logo" />
          <h2>Forgot Password</h2>
          <p>Secure password recovery</p>
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <form onSubmit={handleSendOtp} className="login-form">
            <div className="login-field">
              <label>Email</label>
              <input
                type="email"
                placeholder="Enter your email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <button type="submit" className="login-button">
              Send OTP
            </button>
          </form>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <form onSubmit={handleVerifyOtp} className="login-form">
            <div
              className="login-field text-center"
              onPaste={handleOtpPaste}
            >
              <label>Enter OTP</label>

              <div className="d-flex justify-content-center gap-3 mt-2">
                {otp.map((val, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={val}
                    onChange={(e) =>
                      handleOtpChange(e.target.value, i)
                    }
                    onKeyDown={(e) => handleKeyDown(e, i)}
                    style={{
                      width: "45px",
                      height: "45px",
                      textAlign: "center",
                      fontSize: "20px",
                    }}
                  />
                ))}
              </div>
            </div>

            <button type="submit" className="login-button">
              Verify OTP
            </button>
          </form>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <form onSubmit={handleChangePassword} className="login-form">
            <div className="login-field">
              <label>New Password</label>
              <div className="password-box">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <button type="submit" className="login-button">
              Update Password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
